import React, { useState, useEffect } from 'react';
import { 
  X, Check, Loader2, PieChart as PieChartIcon, 
  Briefcase, Lightbulb, Save, AlertTriangle, CheckCircle 
} from 'lucide-react';
import { listenToData } from '../firebase';
import { calculateHolland } from '../utils/Holland_engine';

const TestForm = ({ test, onSubmit, onCancel, user }) => { 
  const [localAns, setLocalAns] = useState(() => {
    // Sayfa açılır açılmaz hafızada yarım kalmış bir test var mı diye bak!
    if (user && user.no && test.id) {
      const draftId = `test_draft_${user.no}_${test.id}`;
      const savedDraft = sessionStorage.getItem(draftId);
      if (savedDraft) {
        try {
          return JSON.parse(savedDraft);
        } catch (e) {
          return {}; // Eğer hafızadaki veri bozulmuşsa boş başla
        }
      }
    }
    return {}; // Hiçbir kayıt yoksa tertemiz boş başla
  });
  const [page, setPage] = useState(0); 
  const [classList, setClassList] = useState([]); 
  
  // --- YENİ EKLENEN KISIM: Sonuç Ekranı ve Yükleniyor Durumu ---
  const [showResult, setShowResult] = useState(false);
  const [hollandResult, setHollandResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // AĞ BEKLEME DURUMU
  // ------------------------------------------------
  
const questionsPerPage = 150; 
  // 🚀 YENİ: Koşullu soruları (Evet/Hayır) dinamik olarak filtreleyen motor
  const visibleQuestions = (test.questions || []).filter(q => {
    if (q.condition && localAns[q.condition.dependsOn] !== q.condition.value) return false;
    return true;
  });
  const currentQuestions = visibleQuestions.slice(page * questionsPerPage, (page + 1) * questionsPerPage);
  
  // SOSYOMETRİ İÇİN SINIF LİSTESİ
  useEffect(() => {
    if (test.type === 'sociometry' && user) {
      const unsubscribe = listenToData("classes", (data) => {
        const targetClass = data.find(c => c.name === user.class.toUpperCase() && c.clinicianCode === user.clinicianCode);
        if (targetClass) {
          const classmates = targetClass.students.filter(s => s.no.toString() !== user.no.toString());
          setClassList(classmates);
        }
      });
      return () => unsubscribe();
    }
  }, [test.type, user]);

useEffect(() => {
    // Sadece öğrenci varsa ve teste başlamışsa kaydet
    if (user && user.no && test.id && Object.keys(localAns).length > 0) {
      const draftId = `test_draft_${user.no}_${test.id}`;
      // Veriyi anında localStorage veya sessionStorage içine gömüyoruz
      sessionStorage.setItem(draftId, JSON.stringify(localAns));
    }
  }, [localAns, user, test.id]);

  const handleText = (qid, val) => {
    if (qid === 301) val = val.toUpperCase();
    setLocalAns(prev => ({ ...prev, [qid]: val }));
  };
  
  const toggleCheck = (qid) => { setLocalAns(prev => { const newVal = !prev[qid]; if (!newVal) { const copy = { ...prev }; delete copy[qid]; return copy; } return { ...prev, [qid]: true }; }); }; 
  
  const handleComplete = async () => {

    if (isSubmitting) return;

    // Holland önce kendi sonuç ekranını açar.
    // Henüz Firebase kaydı yapılmadığı için taslağı silme.
    if (test.id === 'holland_inventory') {

      const result = calculateHolland(localAns);

      setHollandResult(result);
      setShowResult(true);

      return;
    }

    setIsSubmitting(true);

    try {

      const saveSuccess = await onSubmit(localAns);

      // Yalnızca başarılı Firebase kaydında taslağı temizle.
      if (saveSuccess === true && user?.no && test?.id) {

        sessionStorage.removeItem(
          `test_draft_${user.no}_${test.id}`
        );

      }

    } catch (error) {

      console.error("Test kayıt hatası:", error);

      alert(
        "Kayıt tamamlanamadı.\n\n" +
        "Cevaplarınız korunuyor. Lütfen tekrar deneyin."
      );

    } finally {

      setIsSubmitting(false);

    }

  };

const handleCancelAction = () => {
    if (window.confirm("Testi iptal etmek üzeresiniz. İşaretlediğiniz tüm cevaplar silinecektir. Emin misiniz?")) {
      if (user && user.no && test.id) {
        sessionStorage.removeItem(`test_draft_${user.no}_${test.id}`);
      }
      onCancel();
    }
  };

  const handleSaveAndExit = async () => {

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {

      const saveSuccess = await onSubmit(
        localAns,
        { skipStudentFeedback: true }
      );

      if (saveSuccess === true && user?.no && test?.id) {

        sessionStorage.removeItem(
          `test_draft_${user.no}_${test.id}`
        );

      }

    } catch (error) {

      console.error("Holland kayıt hatası:", error);

      alert(
        "Kayıt tamamlanamadı.\n\n" +
        "Cevaplarınız korunuyor. Lütfen tekrar deneyin."
      );

    } finally {

      setIsSubmitting(false);

    }

  };

  // --- EĞER HOLLAND SONUCU AÇIKSA BUNU GÖSTER ---
  if (showResult && hollandResult) {

    const hollandTop3Subjects = [...(hollandResult.chartData || [])]
        .sort((a, b) => b.A - a.A)
        .slice(0, 3)
        .map(item => item.subject);

    const feedback = hollandResult.studentFeedback || null;

    return (
        <div className="bg-white rounded-[2rem] shadow-2xl border border-slate-200 flex flex-col overflow-hidden h-[95dvh] md:h-[90vh] no-print relative animate-in fade-in zoom-in-95 duration-500">
            {/* Arka Plan Süsü */}
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-indigo-600 to-purple-700 z-0"></div>
            
            <div className="relative z-10 px-6 pt-6 pb-4 flex justify-between items-center text-white shrink-0">
                <h2 className="text-2xl md:text-3xl font-black tracking-tight">{test.name} Sonucu</h2>
            </div>

            {/* İÇERİK ALANI (KAYDIRILABİLİR) */}
            <div className="flex-1 overflow-y-auto p-4 md:p-10 bg-slate-50 relative z-10 rounded-t-3xl md:rounded-t-[2.5rem] custom-scrollbar">
                <div className="max-w-5xl mx-auto space-y-6 md:space-y-8 pb-4">
                    
                    {/* 1. BAŞLIK VE KOD KARTI */}
                    <div className="bg-white p-6 md:p-10 rounded-[2rem] shadow-xl text-center border border-slate-100 relative overflow-hidden group hover:shadow-2xl transition duration-500">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
                        <h3 className="text-slate-500 font-bold uppercase tracking-widest text-xs md:text-sm mb-4">Mesleki İlgi Kodunuz (RIASEC)</h3>
                        <div className="inline-block relative">
                            <div className="absolute -inset-4 bg-indigo-100 rounded-full blur-xl opacity-50 group-hover:opacity-100 transition duration-500"></div>
                            <div className="relative text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 tracking-widest">
                                {hollandResult.summary.split(': ')[1]}
                            </div>
                        </div>
                        {feedback?.highlights?.length > 0 && (
  <div className="flex flex-wrap justify-center gap-2 mt-5">
    {feedback.highlights.map((item, index) => (
      <span
        key={`${item}-${index}`}
        className="px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs md:text-sm font-bold"
      >
        {item}
      </span>
    ))}
  </div>
)}
  </div>
{/* 🌟 ÖĞRENCİYE KİŞİSEL HOLLAND GERİ BİLDİRİMİ */}
{feedback && (
  <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-6 md:p-8 rounded-[2rem] border border-violet-100 shadow-sm">

    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-2xl bg-white border border-violet-100 flex items-center justify-center shrink-0 shadow-sm">
        <Lightbulb className="h-5 w-5 text-violet-600" />
      </div>

      <div>
        <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.18em] text-violet-500 mb-1">
          {feedback.eyebrow || "MESLEKİ İLGİ PROFİLİN"}
        </p>

        <h3 className="text-xl md:text-2xl font-black text-slate-900">
          {feedback.headline || feedback.title}
        </h3>
      </div>
    </div>

    <p className="mt-5 text-sm md:text-base text-slate-700 font-medium leading-relaxed">
      {feedback.message}
    </p>

    {feedback.counselorNote && (
      <div className="mt-5 bg-white/70 border border-violet-100 rounded-2xl px-4 py-3 text-xs md:text-sm text-slate-600 leading-relaxed">
        <strong className="text-violet-700">Unutma:</strong>{" "}
        {feedback.counselorNote}
      </div>
    )}

  </div>
)}

                   {/* 2. GRAFİK + MESLEKLER BÖLÜMÜ */}
<div className="grid md:grid-cols-2 gap-6 md:gap-8">
                        <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-xl border border-slate-100 flex flex-col">
                            <h3 className="font-bold text-slate-800 mb-6 flex items-center text-base md:text-lg"><PieChartIcon className="h-5 w-5 mr-2 text-indigo-500"/> Mesleki İlgi Profilin</h3>
                            <div className="space-y-4 flex-1 justify-center flex flex-col">
                                {hollandResult.chartData.map((item, idx) => {

  const isTop3 = hollandTop3Subjects.includes(item.subject);

  const scoreText =
    item.rawScore != null && item.maxScore != null
      ? `${item.rawScore}/${item.maxScore} · ${Math.round(item.A)}%`
      : `${Math.round(item.A)}%`;

  return (
    <div key={idx}>
      <div className="flex justify-between text-xs font-bold text-slate-600 mb-2">
        <span className={isTop3 ? "text-slate-900 font-black" : ""}>
          {item.subject}
        </span>

        <span className={isTop3 ? "text-indigo-600 font-black" : "text-slate-500"}>
          {scoreText}
        </span>
      </div>

      <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden shadow-inner">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-out ${
            isTop3
              ? "bg-gradient-to-r from-indigo-500 to-purple-500"
              : "bg-slate-300"
          }`}
          style={{ width: `${Math.min(item.A, 100)}%` }}
        />
      </div>
    </div>
  );
})}

<div className="mt-5 pt-4 border-t border-slate-100">
  <p className="text-[10px] md:text-[11px] text-slate-400 leading-relaxed">
    Her ilgi alanı kendi maksimum puanı üzerinden ayrı ayrı değerlendirilir.
    Bu nedenle yüzdeler birbirinden bağımsızdır ve toplamlarının %100 olması beklenmez.
  </p>
</div>
                            </div>
                        </div>

                        {/* 3. ÖNERİLEN MESLEKLER */}
                        <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-xl border border-slate-100 flex flex-col">
                            <h3 className="font-bold text-slate-800 mb-6 flex items-center text-base md:text-lg"><Briefcase className="h-5 w-5 mr-2 text-green-500"/> Size Uygun Meslek Dalları</h3>
                            <div className="flex flex-wrap gap-2 mb-6">
                                {hollandResult.suggestedJobs && hollandResult.suggestedJobs.map((job, i) => (
                                <div key={i} className="bg-green-50 text-green-700 px-3 py-2 rounded-xl text-xs md:text-sm font-bold border border-green-100 shadow-sm hover:scale-105 hover:bg-green-100 transition cursor-default">
                                    {job}
                                </div>
                                ))}
                            </div>
                            <div className="mt-auto bg-amber-50 p-4 rounded-2xl border border-amber-100 text-[11px] text-amber-800 leading-relaxed flex gap-2">
                                <Lightbulb className="h-4 w-4 text-amber-600 shrink-0"/>
                                <div>
                                    <strong className="block mb-1 text-amber-900 font-bold">Tavsiye:</strong>
                                    Bu meslekler kişilik tipinize (RIASEC) göre istatistiksel olarak önerilmiştir. Kesin seçim yapmadan önce meslekleri detaylı araştırmanız önerilir.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ÇIKIŞ BUTONU (SABİTLENDİ - EKRANIN ALTINDAN KAÇMAZ) */}
            <div className="p-4 bg-white border-t border-slate-200 shrink-0 z-20 pb-safe">
                <button onClick={handleSaveAndExit} disabled={isSubmitting} className="w-full md:w-auto bg-slate-900 text-white px-6 py-4 rounded-2xl font-bold text-base hover:bg-black transition shadow-lg flex items-center justify-center mx-auto">
                    {isSubmitting ? <Loader2 className="animate-spin h-5 w-5 mr-3"/> : <Save className="h-5 w-5 mr-3"/>}
                    {isSubmitting ? "Kaydediliyor..." : "Sonuçlarımı Kaydet ve Bitir"}
                </button>
            </div>
        </div>
      );
  }
  
  // --- NORMAL TEST FORMU İSKELETİ ---
  
// 🎯 YENİ: İlerleme Çubuğu ve Motivasyon Sözleri
  const totalQuestions = visibleQuestions.length;
  const answeredCount = Object.keys(localAns).length;
  const progressPct = totalQuestions === 0 ? 0 : Math.round((answeredCount / totalQuestions) * 100);

  let motivationMsg = "Harika bir başlangıç! Kendini keşfetmeye başla. 🚀";
  if (progressPct === 100) motivationMsg = "Muazzam! Bütün soruları tamamladın. 🎉";
  else if (progressPct >= 80) motivationMsg = "Çok az kaldı, harika gidiyorsun! Biraz daha sabır. 🌟";
  else if (progressPct >= 50) motivationMsg = "Yarıladın bile! Kendini keşfetmeye devam et. 💪";
  else if (progressPct >= 25) motivationMsg = "Çok iyi gidiyorsun, tempoyu bozma! ✨";

  return (

    <div className="bg-white rounded-2xl md:rounded-3xl shadow-lg border border-slate-200 flex flex-col relative no-print min-h-[80vh]">
      
      {/* ÜST BAŞLIK VE İLERLEME ÇUBUĞU (SABİT) */}
      <div className="bg-white border-b border-slate-200 shrink-0 z-30 sticky top-0 rounded-t-2xl md:rounded-t-3xl shadow-sm">
        <div className="p-4 md:p-6 flex justify-between items-center bg-slate-50 rounded-t-2xl md:rounded-t-3xl">
            <div>
              <h2 className="font-black text-lg md:text-xl text-slate-800 tracking-tight">{test.name}</h2>
            </div>
            <button onClick={handleCancelAction} disabled={isSubmitting} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><X className="h-6 w-6 text-slate-400"/></button>
        </div>
        
       {/* 🎯 YENİ: Dinamik İlerleme Çubuğu ve Motivasyon */}
        <div className="bg-white px-4 md:px-6 py-3 shadow-sm border-t border-slate-100 flex flex-col md:flex-row items-center gap-2 md:gap-4">
           <div className="flex-1 w-full">
             <div className="flex justify-between items-end mb-2">
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100">{motivationMsg}</span>
                <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">{answeredCount} / {totalQuestions}</span>
             </div>
             <div className="bg-slate-100 rounded-full h-2.5 overflow-hidden border border-slate-200">
                <div 
                  className="bg-blue-600 h-full rounded-full transition-all duration-500 ease-out" 
                  style={{ width: `${progressPct}%` }}
                ></div>
            </div>
           </div>
        </div>
      </div>
      
      {test.intro && <div className="p-4 md:p-5 bg-blue-50/50 text-slate-700 text-sm font-medium border-b border-blue-100 tracking-wide leading-relaxed shrink-0">{test.intro}</div>}

      {test.id === 'ogrenci_bilgi_formu' && user && (
  <div className="px-4 md:px-8 pt-4 bg-slate-50/30">
    <div className="max-w-5xl mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      
      <div>
        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">
          Öğrenci Bilgisi Doğrulandı
        </p>

        <p className="font-black text-slate-800 text-base">
          {user.fullName || 'Öğrenci'}
        </p>

        <p className="text-xs font-semibold text-slate-500 mt-1">
          {user.class || 'Sınıf belirtilmemiş'} • Okul No: {user.no || '-'}
        </p>
      </div>

      <div className="flex items-center text-emerald-700 text-xs font-bold">
        <CheckCircle className="h-5 w-5 mr-2" />
        Okul listesiyle eşleştirildi
      </div>

    </div>
  </div>
)}
      
     {/* SORULAR ALANI (DOĞAL AKIŞ) */}
      {/* 🚀 YENİ: Genişlik 4xl'den 5xl'e çıkarıldı ve kutu içi kaydırma (overflow) iptal edildi */}
      <div className="flex-1 p-4 md:p-8 bg-slate-50/30">
        <div className="space-y-4 md:space-y-6 max-w-5xl mx-auto pb-4">
          {test.type === 'sociometry' && classList.length === 0 && (
            <div className="bg-red-50 text-red-700 p-4 rounded-xl mb-4 border border-red-200">
              <p className="font-bold flex items-center"><AlertTriangle className="h-5 w-5 mr-2"/> Sınıf listesi bulunamadı!</p>
              <p className="text-sm mt-1">Rehber öğretmeniniz henüz sınıf listenizi sisteme yüklememiş olabilir.</p>
            </div>
          )}

          {currentQuestions.map((q, i) => {
            
            // 1. DURUM: CHECKLIST (Güçlendirilmiş Dolgun Tasarım)
            if (test.type === 'checklist') {
              return (
                <div key={q.id} className={`flex items-center p-4 md:p-6 rounded-2xl border shadow-sm transition-all duration-200 ${localAns[q.id] ? 'bg-blue-50 border-blue-400 ring-1 ring-blue-400/50' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                  <label className="cursor-pointer shrink-0 mr-4 md:mr-5 p-2 -ml-2 rounded-xl hover:bg-white/50 transition-colors">
                    <div className={`w-8 h-8 rounded-xl border-2 flex items-center justify-center transition-all duration-200 ${localAns[q.id] ? 'bg-blue-600 border-blue-600 scale-110 shadow-md' : 'bg-white border-slate-300'}`}>
                      {localAns[q.id] && <Check className="h-5 w-5 text-white stroke-[3]" />}
                    </div>
                    <input type="checkbox" className="hidden" checked={!!localAns[q.id]} onChange={() => toggleCheck(q.id)} />
                  </label>
                  <div className="flex-1 select-none">
                    <span className="text-slate-400 font-black text-xs md:text-sm mr-3 bg-slate-100 px-2 py-1 rounded-md">#{q.id}</span>
                    <span className={`font-medium text-sm md:text-base leading-relaxed ${localAns[q.id] ? 'text-blue-900 font-bold' : 'text-slate-700'}`}>
                      {q.text.replace(/\(KRİTİK\)/g, '').trim()}
                    </span>
                  </div>
                </div>
              );
            }

            // 2. DURUM: DİĞER TÜM TESTLER
            return (
              <div key={q.id} className={`p-5 md:p-8 rounded-2xl border shadow-sm transition-colors duration-300 ${localAns[q.id] ? 'bg-white border-slate-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                <p className="font-bold text-slate-800 mb-5 text-base md:text-lg leading-relaxed flex items-start">
                  <span className="text-blue-500 mr-3 shrink-0 mt-0.5">{i + 1}.</span>
                  {q.text}
                </p>
                
                {/* 🎯 YENİ: LIKERT & RADIO (Geniş Seçilebilir Modern Kartlar) */}
                {(test.type === 'likert' || (test.type === 'mix' && q.type === 'radio')) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {(q.options || test.options).map((opt) => {
                      const isSelected = localAns[q.id] === opt.value;
                      return (
                        <label 
                          key={opt.value} 
                          style={{ WebkitTapHighlightColor: 'transparent' }} 
                          className={`flex items-center p-3 md:p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 select-none
                            ${isSelected 
                              ? 'bg-blue-50 border-blue-500 text-blue-900 shadow-md scale-[1.02] ring-1 ring-blue-500/30' 
                              : 'bg-white border-slate-100 text-slate-600 hover:border-blue-300 hover:bg-slate-50'}`}
                        >
                          <input 
                            type="radio" 
                            className="hidden" 
                            checked={isSelected} 
                            onChange={() => handleText(q.id, opt.value)} 
                          />
                          {/* Özel Çizim Radyo Noktası */}
                          <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-blue-600' : 'border-slate-300'}`}>
                              {isSelected && <div className="w-2.5 h-2.5 bg-blue-600 rounded-full" />}
                          </div>
                          <span className={`text-sm md:text-base ${isSelected ? 'font-bold' : 'font-medium'}`}>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* SELECT & SOSYOMETRİ (İzole, sadece stili modernleştirildi) */}
                {((test.type === 'mix' && q.type === 'select') || test.type === 'sociometry') && (
                  <select className="w-full p-4 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none bg-slate-50 hover:bg-white transition-colors text-base font-medium text-slate-700 cursor-pointer" value={localAns[q.id] || ''} onChange={(e) => handleText(q.id, e.target.value)}>
                    <option value="">Lütfen seçiniz...</option>
                    {test.type === 'sociometry' 
                      ? classList.map((stu) => (<option key={stu.no} value={`${stu.no} - ${stu.name}`}>{stu.no} - {stu.name}</option>))
                      : q.options.map((opt, idx) => (<option key={idx} value={opt}>{opt}</option>))
                    }
                  </select>
                )}

                {/* DATE (İzole) */}
                {test.type === 'mix' && q.type === 'date' && (<input type="date" className="w-full p-4 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none text-base font-medium text-slate-700 bg-slate-50 hover:bg-white transition-colors" value={localAns[q.id] || ''} onChange={(e) => handleText(q.id, e.target.value)} />)}

               {/* TEXT & OPEN ENDED (İzole, ferahlatıldı) */}
                {(test.type === 'open_ended' || (test.type === 'mix' && (q.type === 'text' || q.type === 'number'))) && (
                  <input type={q.type === 'number' ? 'number' : 'text'} className="w-full p-4 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none text-base font-medium text-slate-700 bg-slate-50 hover:bg-white transition-colors placeholder:text-slate-400" placeholder={q.id===301 ? "Örn: A, B, BİL-1" : "Cevabınızı buraya yazabilirsiniz..."} maxLength={q.id===301 ? 5 : 500} value={localAns[q.id] || ''} onChange={(e) => handleText(q.id, e.target.value)} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      
   {/* ALT BUTONLAR (SABİT) */}
      {/* 🚀 YENİ: Ekranın en altına yapışması için sticky ve bottom-0 eklendi */}
      <div className="p-4 md:p-6 border-t border-slate-200 bg-white shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.05)] flex justify-between shrink-0 z-30 pb-safe sticky bottom-0 rounded-b-2xl md:rounded-b-3xl">
         <button className="px-6 py-3.5 border-2 border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 hover:border-slate-300 transition-colors" onClick={handleCancelAction} disabled={isSubmitting}>İptal Et</button>
         <button onClick={handleComplete} disabled={isSubmitting} className="px-10 py-3.5 rounded-xl font-bold text-white bg-slate-900 hover:bg-black transition-all shadow-lg flex items-center disabled:opacity-70">
            {isSubmitting ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Save className="h-5 w-5 mr-2"/>}
            {isSubmitting ? "Gönderiliyor..." : "Tamamla"}
         </button>
      </div>
    </div>
  ); 
};

export default TestForm;